/**
 * Formats a date string from "DD.MM.YYYY" format to a localized date string.
 * Uses Intl.DateTimeFormat to format the date according to the specified locale and options.
 *
 * @param dateStr - Date string in "DD.MM.YYYY" format (e.g., "24.12.2025")
 * @param locale - BCP 47 language tag (e.g., "de-CH", "fr-CH", "en-US")
 * @param options - Optional Intl.DateTimeFormatOptions to customize the output format.
 *                  Defaults to { day: '2-digit', month: 'long', year: 'numeric' }
 * @returns Localized date string formatted according to the locale and options,
 *          or empty string if the input date cannot be parsed
 *
 * @example
 * formatDate('24.12.2025', 'de-CH')
 * // Returns: "24. Dezember 2025"
 *
 * @example
 * formatDate('01.01.2026', 'fr-CH', { day: 'numeric', month: 'short', year: 'numeric' })
 * // Returns: "1 janv. 2026"
 */
export function formatDate(
    dateStr: string,
    locale: string,
    options: Intl.DateTimeFormatOptions = {
        day: '2-digit',
        month: 'long',
        year: 'numeric',
    }
): string {
    const [day, month, year] = dateStr?.split('.') || [];

    if (day && month && year) {
        const date = new Date(`${year}-${month}-${day}`);
        return new Intl.DateTimeFormat(locale, options).format(date);
    }

    return '';
}

/**
 * Formats an ISO date string to "DD.MM.YYYY" format.
 * @param isoDate
 */
export function formatDateToDDMMYYYY(isoDate: string): string {
    const date = new Date(isoDate);
    if (isNaN(date.getTime())) {
        return 'Invalid Date';
    }
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    return `${day}.${month}.${year}`;
}

/**
 * Formats an ISO date string to "DD.MM.YY" format.
 * @param isoDate
 */
export function formatDateToDDMMYY(isoDate: string): string {
    const date = new Date(isoDate);
    if (isNaN(date.getTime())) {
        return 'Invalid Date';
    }
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear().toString().slice(-2);
    return `${day}.${month}.${year}`;
}

/**
 * Formats a date string from "DD.MM.YYYY" format to ISO "YYYY-MM-DD" format.
 * Also handles dates already in ISO format or other formats.
 * @param dateString Date string in DD.MM.YYYY format or ISO format
 * @returns Date string in YYYY-MM-DD format, or original string if conversion fails
 */
export function formatDateToISO(dateString: string): string {
    if (!dateString) {
        return dateString;
    }

    // Check if it's already in YYYY-MM-DD format
    const isoPattern = /^\d{4}-\d{2}-\d{2}$/;
    if (isoPattern.test(dateString)) {
        return dateString;
    }

    // Try to convert from DD.MM.YYYY format
    const ddmmyyyyPattern = /^(\d{1,2})\.(\d{1,2})\.(\d{4})$/;
    const match = dateString.match(ddmmyyyyPattern);
    if (match && match[1] && match[2] && match[3]) {
        const day = match[1].padStart(2, '0');
        const month = match[2].padStart(2, '0');
        const year = match[3];
        const normalized = `${year}-${month}-${day}`;

        // Validate the date
        const date = new Date(normalized);
        if (!isNaN(date.getTime()) && date.toISOString().startsWith(normalized)) {
            return normalized;
        }
    }

    // Try to parse as ISO date and extract YYYY-MM-DD
    try {
        const date = new Date(dateString);
        if (!isNaN(date.getTime())) {
            const year = date.getFullYear();
            const month = (date.getMonth() + 1).toString().padStart(2, '0');
            const day = date.getDate().toString().padStart(2, '0');
            return `${year}-${month}-${day}`;
        }
    } catch {
        // Ignore errors
    }

    // Return original if all conversions fail
    return dateString;
}
